import { useState, useEffect, useCallback } from "react";
import axios from "axios";
import { ArrowLeft, TrendingUp, TrendingDown, AlertTriangle, DollarSign, BarChart3, ArrowUpCircle, ArrowDownCircle, MinusCircle, Activity, Gauge, Info } from "lucide-react";
import { useNavigate } from "react-router-dom";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer, ReferenceLine, Area, ComposedChart } from "recharts";
import { API } from "../config/api";

export default function MoneyFlowPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [chartTimeframe, setChartTimeframe] = useState("1mo");
  const [loading, setLoading] = useState(true);
  const [chartLoading, setChartLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchMoneyFlowData = useCallback(async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/market/money-flow-indicator`);
      setData(response.data);
      setError(null);
    } catch (err) {
      console.error("Error fetching money flow data:", err);
      setError("Failed to load money flow indicator data");
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchChartData = useCallback(async () => {
    try {
      setChartLoading(true);
      
      // Fetch both price trend and indicators history data
      const [priceTrendRes, indicatorsRes] = await Promise.all([
        axios.get(`${API}/market/price-trend`, {
          params: { timeframe: chartTimeframe },
          timeout: 30000
        }),
        axios.get(`${API}/market/indicators-history?timeframe=${chartTimeframe}`)
      ]);
      
      const priceTrendData = priceTrendRes.data?.chart_data || [];
      const indicatorsData = indicatorsRes.data?.chart_data || [];
      
      // Merge the data - price trend for market index, indicators for money flow calculation
      const mergedData = priceTrendData.map((point, idx) => {
        // Find matching indicators point by time
        const indicatorPoint = indicatorsData.find(p => p.time === point.time) || indicatorsData[idx] || {};
        
        // Calculate money flow from payday_risk, mfi, rsi (401K/Pension timing)
        const paydayRisk = indicatorPoint.payday_risk || 50;
        const mfi = indicatorPoint.mfi || 50;
        const rsi = indicatorPoint.rsi || 50;
        
        // Payday contribution: Higher payday_risk = more outflow (pre-payday selling)
        // Lower payday_risk = more inflow (post-payday 401K contributions)
        const paydayContrib = (50 - paydayRisk) / 50; // Inverted: low risk = high inflow
        const mfiContrib = (mfi - 50) / 50;
        const rsiContrib = (rsi - 50) / 50;
        
        // Weighted combination: Payday 50%, MFI 30%, RSI 20%
        const moneyFlow = (paydayContrib * 0.5 + mfiContrib * 0.3 + rsiContrib * 0.2) * 100;
        
        return {
          ...point,
          money_flow: Math.round(moneyFlow),
          payday_risk: paydayRisk,
          mfi: mfi,
          rsi: rsi
        };
      });
      
      setChartData(mergedData);
    } catch (err) {
      console.error("Error fetching chart data:", err);
    } finally {
      setChartLoading(false);
    }
  }, [chartTimeframe]);

  useEffect(() => {
    fetchMoneyFlowData();
    const interval = setInterval(fetchMoneyFlowData, 300000);
    return () => clearInterval(interval);
  }, [fetchMoneyFlowData]);

  useEffect(() => {
    fetchChartData();
  }, [fetchChartData]);

  const getFlowIcon = (direction) => {
    switch (direction) {
      case "IN":
        return <ArrowUpCircle className="w-8 h-8 text-green-400" />;
      case "OUT":
        return <ArrowDownCircle className="w-8 h-8 text-red-400" />;
      default:
        return <MinusCircle className="w-8 h-8 text-gray-400" />;
    }
  };

  const getScoreGradient = (score) => {
    if (score >= 40) return "from-green-600 to-green-900";
    if (score >= 15) return "from-lime-600 to-lime-900";
    if (score >= -15) return "from-gray-600 to-gray-800";
    if (score >= -40) return "from-orange-600 to-orange-900";
    return "from-red-600 to-red-900";
  };

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="bg-slate-900 border border-slate-700 rounded-lg p-3 shadow-xl">
          <p className="text-gray-400 text-sm mb-2">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} style={{ color: entry.color }} className="text-sm">
              {entry.name}: {entry.value?.toFixed(2)}
              {entry.dataKey === 'market_price' ? '%' : entry.dataKey === 'money_flow' ? '%' : ''}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-950 p-4">
        <div className="max-w-[1920px] mx-auto space-y-4">
          <div className="flex items-center gap-3">
            <div className="animate-pulse bg-gray-800/60 rounded-full h-8 w-8" />
            <div className="animate-pulse bg-gray-800/60 rounded h-6 w-48" />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {[1,2,3,4].map(i => (
              <div key={i} className="bg-[#111] border border-gray-800 rounded-lg p-3">
                <div className="animate-pulse bg-gray-800/60 rounded h-3 w-1/2 mb-2" />
                <div className="animate-pulse bg-gray-800/60 rounded h-8 w-2/3 mb-2" />
                <div className="animate-pulse bg-gray-800/60 rounded h-3 w-3/4" />
              </div>
            ))}
          </div>
          <div className="bg-[#111] border border-gray-800 rounded-lg p-4 h-72">
            <div className="animate-pulse bg-gray-800/60 rounded h-4 w-1/4 mb-4" />
            <div className="flex items-end gap-1 h-[80%]">
              {Array.from({length:20}).map((_,i) => (
                <div key={i} className="animate-pulse bg-gray-800/60 rounded-t flex-1" style={{height: `${20+Math.random()*60}%`}} />
              ))}
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {[1,2].map(i => (
              <div key={i} className="bg-[#111] border border-gray-800 rounded-lg overflow-hidden">
                <div className="bg-[#0d0d0d] px-4 py-2.5 flex gap-4">
                  {[1,2,3].map(c => <div key={c} className="animate-pulse bg-gray-800/60 rounded h-3 flex-1" />)}
                </div>
                {[1,2,3,4,5,6].map(r => (
                  <div key={r} className="px-4 py-2.5 flex gap-4 border-t border-gray-800/50">
                    {[1,2,3].map(c => <div key={c} className="animate-pulse bg-gray-800/60 rounded h-3 flex-1" />)}
                  </div>
                ))}
              </div>
            ))}
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-slate-950 flex items-center justify-center">
        <div className="text-center text-red-400">
          <AlertTriangle className="w-12 h-12 mx-auto mb-4" />
          <p>{error}</p>
          <button
            onClick={fetchMoneyFlowData}
            className="mt-4 px-4 py-2 bg-cyan-600 rounded hover:bg-cyan-500 text-white"
          >
            Retry
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-950 text-white p-4 md:p-8">
      {/* Header */}
      <div className="max-w-6xl mx-auto">
        <button
          onClick={() => window.open("/", "_blank")}
          className="flex items-center gap-2 text-gray-400 hover:text-white mb-6 transition-colors"
          data-testid="back-button"
        >
          <ArrowLeft className="w-5 h-5" />
          Back to Market Overview
        </button>

        <div className="flex items-center gap-4 mb-8">
          <div className="p-3 bg-cyan-900/30 rounded-xl">
            <Activity className="w-8 h-8 text-cyan-400" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-cyan-400">Money In-Flow / Out-Flow Indicator</h1>
            <p className="text-gray-400 text-sm">Tracks institutional money movement based on payday cycles & technical indicators</p>
          </div>
        </div>

        {/* Main Score Card */}
        <div className={`bg-gradient-to-br ${getScoreGradient(data.score)} rounded-2xl p-6 md:p-8 mb-8 shadow-xl`}>
          <div className="flex flex-col md:flex-row items-center justify-between gap-6">
            <div className="text-center md:text-left">
              <div className="text-6xl md:text-8xl font-bold mb-2">
                {data.score > 0 ? "+" : ""}{data.score}%
              </div>
              <div className="text-xl md:text-2xl font-semibold opacity-90">{data.signal}</div>
            </div>
            <div className="flex flex-col items-center md:items-end gap-2">
              <div className="flex items-center gap-3 text-2xl font-bold">
                {getFlowIcon(data.flow_direction)}
                <span className="uppercase">{data.flow_direction} FLOW</span>
              </div>
              <div className="text-sm opacity-80 flex items-center gap-2">
                <Gauge className="w-4 h-4" />
                MFI: {data.components?.mfi?.value} | RSI: {data.components?.rsi?.value}
              </div>
            </div>
          </div>
          <div className="mt-6 p-4 bg-black/20 rounded-xl">
            <p className="text-lg">{data.description}</p>
          </div>
        </div>

        {/* Market Index Chart - Same as Market Indicators Page */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-8" data-testid="money-flow-chart">
          <div className="flex items-center justify-between mb-6">
            <h2 className="text-lg font-semibold text-cyan-400 flex items-center gap-2">
              <BarChart3 className="w-5 h-5" />
              Cap-Weighted Market Index
              <span className="text-xs text-gray-500 font-normal ml-2">SPY 58.5% | QQQ 29.2% | IWM 8.2% | DIA 4.1%</span>
            </h2>
            <div className="flex gap-2">
              {["1d", "5d", "1mo", "3mo", "6mo", "1y"].map((tf) => (
                <button
                  key={tf}
                  onClick={() => setChartTimeframe(tf)}
                  className={`px-3 py-1 text-sm rounded transition-colors ${
                    chartTimeframe === tf
                      ? "bg-cyan-600 text-white"
                      : "bg-slate-800 text-gray-400 hover:bg-slate-700"
                  }`}
                >
                  {tf.toUpperCase()}
                </button>
              ))}
            </div>
          </div>
          
          {/* Chart Legend */}
          <div className="flex flex-wrap gap-4 mb-4 text-sm">
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-cyan-400"></div>
              <span className="text-gray-400">Money Flow (401K/Pension)</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-purple-500"></div>
              <span className="text-gray-400">Market Index Price</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-amber-500"></div>
              <span className="text-gray-400">Regression Trend</span>
            </div>
            <div className="flex items-center gap-2">
              <div className="w-3 h-3 rounded-full bg-blue-500"></div>
              <span className="text-gray-400">SMA 20</span>
            </div>
          </div>

          {chartLoading ? (
            <div className="h-80 flex items-center justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-cyan-400"></div>
            </div>
          ) : chartData.length > 0 ? (
            <div className="h-80">
              <ResponsiveContainer width="100%" height="100%">
                <ComposedChart data={chartData} margin={{ top: 5, right: 50, left: 0, bottom: 5 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#334155" />
                  <XAxis 
                    dataKey="time" 
                    stroke="#64748b" 
                    tick={{ fill: '#94a3b8', fontSize: 11 }}
                    interval="preserveStartEnd"
                  />
                  {/* Left Y-axis for Market Index Price */}
                  <YAxis 
                    yAxisId="price"
                    stroke="#64748b" 
                    tick={{ fill: '#94a3b8', fontSize: 10 }}
                    domain={['auto', 'auto']}
                    tickFormatter={(value) => `$${value}`}
                  />
                  {/* Right Y-axis for Money Flow % */}
                  <YAxis 
                    yAxisId="flow"
                    orientation="right"
                    stroke="#22d3ee" 
                    tick={{ fill: '#22d3ee', fontSize: 10 }}
                    domain={[-60, 60]}
                    tickFormatter={(value) => `${value}%`}
                    label={{ value: 'Money Flow', angle: 90, position: 'insideRight', fill: '#22d3ee', fontSize: 10 }}
                  />
                  <Tooltip 
                    contentStyle={{ 
                      backgroundColor: '#1a1a2e', 
                      border: '1px solid #374151', 
                      borderRadius: '8px',
                      padding: '10px'
                    }}
                    labelStyle={{ color: '#9ca3af', marginBottom: '5px' }}
                    formatter={(value, name) => {
                      if (name === 'Price') return [`$${value?.toFixed(2)}`, 'Index Price'];
                      if (name === 'Regression') return [`$${value?.toFixed(2)}`, 'Trend Line'];
                      if (name === 'SMA 20') return [`$${value?.toFixed(2)}`, name];
                      if (name === 'Money Flow') return [`${value}%`, '401K/Pension Flow'];
                      return [value, name];
                    }}
                  />
                  
                  {/* Reference lines for Money Flow */}
                  <ReferenceLine yAxisId="flow" y={0} stroke="#6b7280" strokeDasharray="5 5" />
                  <ReferenceLine yAxisId="flow" y={30} stroke="#22c55e" strokeDasharray="3 3" opacity={0.3} />
                  <ReferenceLine yAxisId="flow" y={-30} stroke="#ef4444" strokeDasharray="3 3" opacity={0.3} />
                  
                  {/* Money Flow Area - 401K/Pension timing */}
                  <Area
                    yAxisId="flow"
                    type="monotone"
                    dataKey="money_flow"
                    stroke="#22d3ee"
                    strokeWidth={3}
                    fill="url(#moneyFlowGradient)"
                    name="Money Flow"
                  />
                  
                  {/* SMA 20 */}
                  <Line 
                    yAxisId="price"
                    type="monotone" 
                    dataKey="sma20" 
                    stroke="#3b82f6" 
                    strokeWidth={1.5} 
                    dot={false}
                    name="SMA 20"
                  />
                  
                  {/* Regression Line */}
                  <Line 
                    yAxisId="price"
                    type="monotone" 
                    dataKey="regression" 
                    stroke="#f59e0b" 
                    strokeWidth={2} 
                    dot={false}
                    name="Regression"
                    strokeDasharray="8 4"
                  />
                  
                  {/* Price Line - Market Index */}
                  <Line 
                    yAxisId="price"
                    type="monotone" 
                    dataKey="price" 
                    stroke="#a855f7" 
                    strokeWidth={2.5} 
                    dot={false}
                    name="Price"
                    activeDot={{ r: 5, fill: '#a855f7' }}
                  />
                  
                  <defs>
                    <linearGradient id="moneyFlowGradient" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="0%" stopColor="#22d3ee" stopOpacity={0.4}/>
                      <stop offset="50%" stopColor="#22d3ee" stopOpacity={0.1}/>
                      <stop offset="100%" stopColor="#22d3ee" stopOpacity={0.4}/>
                    </linearGradient>
                  </defs>
                </ComposedChart>
              </ResponsiveContainer>
            </div>
          ) : (
            <div className="h-80 flex items-center justify-center text-gray-500">
              No chart data available
            </div>
          )}
          
          <div className="mt-4 text-xs text-gray-500 text-center">
            Cyan area = Money Flow from 401K/Pension timing | Positive = Post-payday inflow | Negative = Pre-payday outflow
          </div>
        </div>

        {/* Grid Layout */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mb-8">
          
          {/* Component Breakdown */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="component-breakdown">
            <h2 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
              <Gauge className="w-5 h-5" />
              Component Breakdown
            </h2>
            <div className="space-y-4">
              {/* Payday Phase */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <DollarSign className="w-5 h-5 text-amber-400" />
                    <span className="font-medium">Payday Phase</span>
                    <span className="text-xs bg-amber-900/50 text-amber-400 px-2 py-0.5 rounded">50% weight</span>
                  </div>
                  <span className="text-lg font-bold text-amber-400">{data.components?.payday?.phase}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-400">
                  <span>Score: {data.components?.payday?.score}</span>
                  <span>Contribution: {data.components?.payday?.contribution}%</span>
                </div>
                <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-amber-500 rounded-full transition-all"
                    style={{ width: `${Math.max(0, Math.min(100, (data.components?.payday?.contribution || 0) + 50))}%` }}
                  ></div>
                </div>
              </div>
              
              {/* MFI */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5 text-green-400" />
                    <span className="font-medium">Money Flow Index (MFI)</span>
                    <span className="text-xs bg-green-900/50 text-green-400 px-2 py-0.5 rounded">30% weight</span>
                  </div>
                  <span className="text-lg font-bold text-green-400">{data.components?.mfi?.value}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-400">
                  <span>{data.components?.mfi?.value > 80 ? "Overbought" : data.components?.mfi?.value < 20 ? "Oversold" : "Neutral"}</span>
                  <span>Contribution: {data.components?.mfi?.contribution}%</span>
                </div>
                <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-green-500 rounded-full transition-all"
                    style={{ width: `${data.components?.mfi?.value || 50}%` }}
                  ></div>
                </div>
              </div>
              
              {/* RSI */}
              <div className="bg-slate-800 rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <div className="flex items-center gap-2">
                    <Activity className="w-5 h-5 text-purple-400" />
                    <span className="font-medium">Relative Strength Index (RSI)</span>
                    <span className="text-xs bg-purple-900/50 text-purple-400 px-2 py-0.5 rounded">20% weight</span>
                  </div>
                  <span className="text-lg font-bold text-purple-400">{data.components?.rsi?.value}</span>
                </div>
                <div className="flex items-center justify-between text-sm text-gray-400">
                  <span>{data.components?.rsi?.value > 70 ? "Overbought" : data.components?.rsi?.value < 30 ? "Oversold" : "Neutral"}</span>
                  <span>Contribution: {data.components?.rsi?.contribution}%</span>
                </div>
                <div className="mt-2 h-2 bg-slate-700 rounded-full overflow-hidden">
                  <div 
                    className="h-full bg-purple-500 rounded-full transition-all"
                    style={{ width: `${data.components?.rsi?.value || 50}%` }}
                  ></div>
                </div>
              </div>
            </div>
          </div>

          {/* Interpretation Guide */}
          <div className="bg-slate-900 border border-slate-800 rounded-xl p-6" data-testid="interpretation-guide">
            <h2 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
              <Info className="w-5 h-5" />
              Interpretation Guide
            </h2>
            <div className="space-y-4">
              {/* Inflow Explanation */}
              <div className="bg-green-900/20 border border-green-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowUpCircle className="w-5 h-5 text-green-400" />
                  <span className="font-semibold text-green-400">Money INFLOW</span>
                </div>
                <p className="text-sm text-gray-300">
                  {data.interpretation?.inflow || "Post-payday periods see 401k contributions and new investments enter the market"}
                </p>
                <div className="mt-2 text-xs text-green-400/70">
                  Typically bullish - consider accumulating positions
                </div>
              </div>
              
              {/* Outflow Explanation */}
              <div className="bg-red-900/20 border border-red-800/50 rounded-lg p-4">
                <div className="flex items-center gap-2 mb-2">
                  <ArrowDownCircle className="w-5 h-5 text-red-400" />
                  <span className="font-semibold text-red-400">Money OUTFLOW</span>
                </div>
                <p className="text-sm text-gray-300">
                  {data.interpretation?.outflow || "Pre-payday periods see selling as people cover expenses before paycheck"}
                </p>
                <div className="mt-2 text-xs text-red-400/70">
                  Typically bearish - consider reducing exposure or hedging
                </div>
              </div>
              
              {/* Technical Signals */}
              <div className="bg-slate-800 rounded-lg p-4">
                <h3 className="font-semibold text-gray-300 mb-3">Technical Signal Thresholds</h3>
                <div className="grid grid-cols-2 gap-3 text-sm">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-gray-400">MFI &gt; 80: Strong buying</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-gray-400">MFI &lt; 20: Strong selling</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                    <span className="text-gray-400">RSI &gt; 70: Overbought</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                    <span className="text-gray-400">RSI &lt; 30: Oversold</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Flow Signal History */}
        <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 mb-8">
          <h2 className="text-lg font-semibold text-cyan-400 mb-4 flex items-center gap-2">
            <BarChart3 className="w-5 h-5" />
            Signal Interpretation
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {[
              { signal: "STRONG INFLOW", range: "+40% to +100%", color: "green", desc: "Heavy institutional buying" },
              { signal: "MODERATE INFLOW", range: "+15% to +40%", color: "lime", desc: "Steady accumulation" },
              { signal: "NEUTRAL FLOW", range: "-15% to +15%", color: "gray", desc: "Balanced market" },
              { signal: "MODERATE OUTFLOW", range: "-40% to -15%", color: "orange", desc: "Steady distribution" },
              { signal: "STRONG OUTFLOW", range: "-100% to -40%", color: "red", desc: "Heavy selling pressure" },
            ].map((item, idx) => (
              <div 
                key={idx}
                className={`p-4 rounded-lg border ${
                  data.signal === item.signal 
                    ? `bg-${item.color}-900/30 border-${item.color}-500` 
                    : 'bg-slate-800 border-slate-700'
                }`}
              >
                <div className={`text-sm font-semibold ${
                  item.color === 'green' ? 'text-green-400' :
                  item.color === 'lime' ? 'text-lime-400' :
                  item.color === 'gray' ? 'text-gray-400' :
                  item.color === 'orange' ? 'text-orange-400' :
                  'text-red-400'
                }`}>
                  {item.signal}
                </div>
                <div className="text-xs text-gray-500 mt-1">{item.range}</div>
                <div className="text-xs text-gray-400 mt-2">{item.desc}</div>
                {data.signal === item.signal && (
                  <div className="mt-2 text-xs bg-cyan-500/20 text-cyan-400 px-2 py-1 rounded inline-block">
                    CURRENT
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        {/* Footer Info */}
        <div className="text-center text-gray-500 text-sm mb-8">
          <p>Last updated: {data.timestamp ? new Date(data.timestamp).toLocaleString() : 'N/A'}</p>
          <p className="mt-2 text-xs">
            The Money Flow indicator combines payday cycle analysis (401k/pension timing) with technical indicators 
            (MFI, RSI) to predict institutional money movement in the market.
          </p>
        </div>
      </div>
    </div>
  );
}
